import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCad, orderItems, orderTotalCents, variantLabel, variantPriceCents } from '@/lib/documentServices';
import { TaxExemptLine } from './DocumentServicesCommon';

export default function OrderSummary({ order, product, variant, quantity = 1, compact = false }) {
  const savedItems = orderItems(order);
  const items = savedItems.length ? savedItems : variant ? [{
    product_name: product?.name,
    variant_name: variantLabel(variant),
    quantity,
    unit_price_cents: variantPriceCents(variant),
    total_cents: variantPriceCents(variant) * quantity,
  }] : [];
  const subtotal = Number(order?.subtotal_cents ?? items.reduce((sum, item) => (
    sum + Number(item?.total_cents ?? item?.line_total_cents ?? (item?.unit_price_cents || 0) * (item?.quantity || 1))
  ), 0)) || 0;
  const tax = Number(order?.tax_amount_cents ?? 0) || 0;
  const total = order ? orderTotalCents(order) : subtotal + tax;

  return (
    <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
      <CardHeader className={compact ? 'pb-2' : 'pb-3'}>
        <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          Order summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {items.map((item, index) => {
            const itemQuantity = Number(item?.quantity || 1);
            const unit = Number(item?.unit_price_cents ?? item?.price_cents ?? item?.price_snapshot_cents ?? 0);
            const line = Number(item?.total_cents ?? item?.line_total_cents ?? unit * itemQuantity);
            return (
              <div key={item?.id || item?.uuid || index} className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-medium text-[#062B1A]">{item?.product_name || item?.name || product?.name || 'Document service'}</p>
                  <p className="mt-0.5 text-xs text-[#062B1A]/50">
                    {item?.variant_name || item?.variant_label || item?.sku || (variant ? variantLabel(variant) : 'Service option')}
                    {itemQuantity > 1 ? ` · Qty ${itemQuantity}` : ''}
                  </p>
                </div>
                <span className="shrink-0 text-sm font-semibold text-[#062B1A]">{formatCad(line)}</span>
              </div>
            );
          })}
        </div>
        <div className="space-y-2 border-t border-[#062B1A]/10 pt-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#062B1A]/60">Subtotal</span>
            <span className="font-medium text-[#062B1A]">{formatCad(subtotal)}</span>
          </div>
          <TaxExemptLine amount={formatCad(tax)} />
          <div className="flex items-center justify-between border-t border-[#062B1A]/10 pt-3">
            <span className="font-semibold text-[#062B1A]">Total CAD</span>
            <span className="text-xl font-bold text-[#062B1A]">{formatCad(total)}</span>
          </div>
        </div>
        <p className="text-xs leading-relaxed text-[#062B1A]/45">
          No card, convenience, processing, or other surcharge is added. The total shown is the amount submitted for payment.
        </p>
      </CardContent>
    </Card>
  );
}
